import { useState } from "react";
import { Dropdown, type DropdownItem } from "../shared";
import type { LearningPlayers, LearningStaff } from "./useLearningPlayers";
import css from "./LearningPlayersRow.module.css";

/**
 * «Кто играет» — ряд модалки брони при включённом «Учеб» (13.10, владелец), по образцу ряда спецверсии:
 * поле-кнопка с выбранными именами → общий Dropdown сразу мультивыбором с фильтром (прецедент eo/DictBlocks).
 * Мета пункта: «новый» — принят на работу недавно; «уволен» — уже игравший уволенный (остаётся в истории игры).
 * Telegram и роль оператора есть у всех принятых (владелец 13.10: «так не бывает — принимаем только с
 * телеграмом и сразу выдаём роль оператор»), поэтому «не напишет отчёт» не помечаем. Оператору (403) ряд не показываем.
 */
export interface LearningPlayersRowProps {
  lp: LearningPlayers;
  /** Шелл поля формы (css.input BookingForm) — кнопка выглядит как соседние поля. */
  inputClass?: string;
}

const metaOf = (s: LearningStaff): string | undefined =>
  [s.is_new ? "новый" : "", s.gone ? "уволен" : ""].filter(Boolean).join(" · ") || undefined;

export function LearningPlayersRow({ lp, inputClass = "" }: LearningPlayersRowProps) {
  const [dd, setDd] = useState<HTMLElement | null>(null);
  if (lp.hidden) return null;

  const items: DropdownItem[] = lp.pool.map((s) => ({ id: s.id, label: s.name, meta: metaOf(s) }));
  const chosen = lp.ids.map((id) => lp.pool.find((s) => s.id === id)).filter((s): s is LearningStaff => !!s);
  const text = lp.loading ? "загрузка…"
    : lp.failed ? "не загрузилось — нажмите ещё раз"
    : chosen.length ? chosen.map((s) => s.name).join(", ") : "выбрать сотрудников";

  return (
    <div className={css.wrap}>
      <div className={css.row}>
        <span className={css.lbl}>Кто играет</span>
        <button
          type="button"
          className={`${inputClass} ${css.trigger} ${chosen.length ? "" : css.triggerEmpty}`}
          disabled={lp.loading}
          onClick={(e) => (lp.failed ? lp.retry() : setDd(e.currentTarget))}
          aria-haspopup="listbox"
          aria-expanded={!!dd}
          aria-label={`Кто из сотрудников играет: ${text}`}
        >
          <span className={css.names}>{text}</span>
        </button>
      </div>
      <div className={css.hint}>Учебная игра бесплатна — выручку не ждём, задаток не берут. Отчёты друг о друге запрашивает ОО в расстановке</div>
      {lp.over ? <div className={`${css.hint} ${css.warn}`}>{lp.over}</div> : null}
      {/* «готово» с пустым выбором Dropdown не применяет — снять всех можно явным пунктом */}
      <Dropdown
        open={!!dd} anchor={dd} multiDefault filter
        items={items} selectedIds={lp.ids}
        action={lp.ids.length ? { label: "Никто не играет — очистить", onPress: () => lp.set([]) } : undefined}
        onClose={() => setDd(null)}
        onCommit={(ids) => lp.set(ids.map(String))}
      />
    </div>
  );
}
